import { useState } from "react";
import { motion } from "motion/react";
import {
  ArrowRight,
  Globe,
  Search,
  Database,
  Brain,
  FileText,
  MessageSquare,
  Sparkles,
  List,
  Merge,
  Upload,
  Scissors,
} from "lucide-react";
import type { WorkflowDefinition, WorkflowStep } from "@/data/prompts";
import { PROMPTS } from "@/data/prompts";
import PromptDetailModal from "./PromptDetailModal";

const STEP_ICONS: Record<WorkflowStep["icon"], React.ElementType> = {
  globe: Globe,
  search: Search,
  scrape: Scissors,
  upload: Upload,
  database: Database,
  brain: Brain,
  file: FileText,
  chat: MessageSquare,
  sparkles: Sparkles,
  list: List,
  merge: Merge,
};

interface WorkflowDiagramProps {
  workflow: WorkflowDefinition;
}

export default function WorkflowDiagram({ workflow }: WorkflowDiagramProps) {
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [pickerStepId, setPickerStepId] = useState<string | null>(null);

  const handleStepClick = (step: WorkflowStep) => {
    if (step.promptIds.length === 0) return;
    if (step.promptIds.length === 1) {
      setSelectedPromptId(step.promptIds[0]);
      setPickerStepId(null);
    } else {
      setPickerStepId(pickerStepId === step.id ? null : step.id);
    }
  };

  const handlePromptSelect = (promptId: string) => {
    setSelectedPromptId(promptId);
    setPickerStepId(null);
  };

  const selectedPrompt = selectedPromptId ? PROMPTS[selectedPromptId] : null;

  return (
    <div className="w-full">
      {/* Desktop - Horizontal */}
      <div className="hidden lg:flex items-start justify-center gap-2">
        {workflow.steps.map((step, index) => {
          const hasPrompts = step.promptIds.length > 0;
          return (
            <div key={step.id} className="flex items-start">
              <StepNode
                step={step}
                index={index}
                hasPrompts={hasPrompts}
                isPickerOpen={pickerStepId === step.id}
                onClick={() => handleStepClick(step)}
                onPromptSelect={handlePromptSelect}
              />
              {index < workflow.steps.length - 1 && (
                <motion.div
                  className="flex items-center mt-12 mx-1"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.15, duration: 0.4 }}
                >
                  <div className="w-10 h-0.5 bg-gradient-to-r from-border to-border/50 relative overflow-hidden">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/40 to-transparent"
                      animate={{ x: ["-100%", "200%"] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground/50 -ml-1" />
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile - Vertical */}
      <div className="lg:hidden space-y-3">
        {workflow.steps.map((step, index) => {
          const hasPrompts = step.promptIds.length > 0;
          return (
            <div key={step.id}>
              <StepNode
                step={step}
                index={index}
                hasPrompts={hasPrompts}
                isPickerOpen={pickerStepId === step.id}
                onClick={() => handleStepClick(step)}
                onPromptSelect={handlePromptSelect}
              />
              {index < workflow.steps.length - 1 && (
                <motion.div
                  className="flex flex-col items-center py-2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                >
                  <div className="h-6 w-0.5 bg-gradient-to-b from-border to-border/50 relative overflow-hidden">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/40 to-transparent"
                      animate={{ y: ["-100%", "200%"] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </div>
                  <ArrowRight className="w-3 h-3 text-muted-foreground/50 rotate-90 -mt-0.5" />
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      {/* Prompt Detail Modal */}
      {selectedPrompt && (
        <PromptDetailModal
          prompt={selectedPrompt}
          onClose={() => setSelectedPromptId(null)}
        />
      )}
    </div>
  );
}

function StepNode({
  step,
  index,
  hasPrompts,
  isPickerOpen,
  onClick,
  onPromptSelect,
}: {
  step: WorkflowStep;
  index: number;
  hasPrompts: boolean;
  isPickerOpen: boolean;
  onClick: () => void;
  onPromptSelect: (id: string) => void;
}) {
  const Icon = STEP_ICONS[step.icon] || Sparkles;

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
    >
      <div
        onClick={hasPrompts ? onClick : undefined}
        className={`
          relative p-4 rounded-xl border transition-all duration-200
          lg:w-52 w-full
          ${hasPrompts
            ? "cursor-pointer border-primary/30 bg-card hover:bg-muted/50 hover:border-primary/50 shadow-sm"
            : "border-border bg-card/50 opacity-70"
          }
        `}
      >
        {/* Sparkle indicator for prompt-enabled steps */}
        {hasPrompts && (
          <Sparkles className="absolute top-2 right-2 w-3.5 h-3.5 text-amber-500" />
        )}

        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${step.color}20` }}
          >
            <Icon className="w-4 h-4" style={{ color: step.color }} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{step.label}</p>
            <p className="text-[10px] text-muted-foreground">{step.service}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
          {step.description}
        </p>

        {hasPrompts && (
          <p className="text-[10px] text-primary mt-2">
            {step.promptIds.length === 1
              ? "Click to view prompt"
              : `${step.promptIds.length} prompts - click to view`}
          </p>
        )}
      </div>

      {/* Multi-prompt picker dropdown */}
      {isPickerOpen && step.promptIds.length > 1 && (
        <motion.div
          className="absolute z-50 mt-2 w-full lg:w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div className="p-2 max-h-60 overflow-y-auto">
            {step.promptIds.map(promptId => {
              const prompt = PROMPTS[promptId];
              if (!prompt) return null;
              return (
                <button
                  key={promptId}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromptSelect(promptId);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted/50 transition-colors"
                >
                  {prompt.name}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
